import { useState, useEffect } from 'react';

// "Is this a phone?" detection for the phone-only UI.
//
// Real users: a small viewport AND a coarse (touch) pointer. The touch part is
// deliberate — it means a desktop user who merely narrows their browser window
// does NOT get the phone UI; only actual touch devices do.
//
// Dev/preview override (so the phone UI can be inspected on a normal computer):
//   ?mobile=1  -> force phone UI on    |  ?mobile=0  -> force phone UI off
//   localStorage 'forceMobile' = '1' / '0' does the same, persistently.
const MOBILE_QUERY = '(max-width: 640px) and (pointer: coarse)';

function readForced() {
  try {
    const param = new URLSearchParams(window.location.search).get('mobile');
    if (param === '1') return true;
    if (param === '0') return false;
    const stored = window.localStorage.getItem('forceMobile');
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    /* SSR / storage blocked — fall through to media query */
  }
  return null;
}

function evaluate() {
  const forced = readForced();
  if (forced !== null) return forced;
  try {
    return window.matchMedia(MOBILE_QUERY).matches;
  } catch {
    return false;
  }
}

export default function useIsMobile() {
  const [isMobile, setIsMobile] = useState(evaluate);

  useEffect(() => {
    const update = () => setIsMobile(evaluate());
    let mql;
    try {
      mql = window.matchMedia(MOBILE_QUERY);
      if (mql.addEventListener) mql.addEventListener('change', update);
      else mql.addListener(update);
    } catch {
      /* ignore */
    }
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener('change', update);
        else mql.removeListener(update);
      }
    };
  }, []);

  return isMobile;
}
